import { type Dispatch, type SetStateAction } from 'react';

import { Button, Dialog, RadixDialogPrimitives, Typography } from '@capsule/consumer-design-system';

import { useHistory } from 'react-router';
import { css } from 'styled-components';

import ChevronIcon from 'public/new_chevron.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { useLoggedInTransferFormData } from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { AccountPageAddDependent } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import type { RenderedStepComponentProps } from '..';
import Step from '../Step';
import { STEP_NAME } from '../types';

import { StethoscopeArt, StyledArrow, PillBottleArt, TextColumn, LinkWithIcon, Content, ButtonContent } from './styles';

interface LoggedInTransferIntroProps extends RenderedStepComponentProps {
  setSteps: Dispatch<SetStateAction<STEP_NAME[]>>;
}

export const LoggedInTransferIntro = ({ handleNextClick, setSteps, patients }: LoggedInTransferIntroProps) => {
  useSegmentEvent('viewTransferRxIntent', { shouldTrackOnMount: true });
  const history = useHistory();

  const { loggedInTransferFormData } = useLoggedInTransferFormData();

  /*
   * goToPharmacyStep & goToDoctorStep:
   *   Remove the initial placeholder, if it is there. Also remove the step not chosen, in
   *   case the user goes forward then back to the intro step to choose a different option.
   */
  const goToPharmacyStep = () => {
    setSteps(prevSteps => {
      const introIndex = prevSteps.findIndex(step => step === STEP_NAME.INTRO);
      const newSteps = prevSteps.filter(step => step !== STEP_NAME.PLACEHOLDER && step !== STEP_NAME.DOCTOR);
      if (!newSteps.includes(STEP_NAME.PHARMACY)) {
        return [...newSteps.slice(0, introIndex + 1), STEP_NAME.PHARMACY, ...newSteps.slice(introIndex + 1)];
      }
      return newSteps;
    });
    handleNextClick();
  };

  const goToDoctorStep = () => {
    setSteps(prevSteps => {
      const introIndex = prevSteps.findIndex(step => step === STEP_NAME.INTRO);
      const newSteps = prevSteps.filter(step => step !== STEP_NAME.PLACEHOLDER && step !== STEP_NAME.PHARMACY);
      if (!newSteps.includes(STEP_NAME.DOCTOR)) {
        return [...newSteps.slice(0, introIndex + 1), STEP_NAME.DOCTOR, ...newSteps.slice(introIndex + 1)];
      }
      return newSteps;
    });
    handleNextClick();
  };

  const title =
    loggedInTransferFormData?.patientIsPrimary || !loggedInTransferFormData?.patientName
      ? 'Where should we get your medications from?'
      : `Where should we get ${loggedInTransferFormData?.patientName}’s medications from?`;

  return (
    // Regular whitespace caused the sub-title to not render at all, which broke the layout. So U+200E it is.
    <Step title={title} text="‎ ">
      <Content>
        <Button
          variant="outline-lg"
          isFullWidth={true}
          onClick={goToPharmacyStep}
          css={css`
            margin: 0 0 2rem 0;
          `}
        >
          <ButtonContent>
            <PillBottleArt />
            <TextColumn>
              <Typography variant="heading4">Transfer from another pharmacy</Typography>
              <Typography
                variant="small"
                color={'capsuleBlue30'}
                css={css`
                  margin: 0.5rem 0 0 0;
                  font-weight: 300;
                `}
              >
                We’ll contact your pharmacy to move your remaining refills.
              </Typography>
            </TextColumn>
            <StyledArrow className="arrow" src={ChevronIcon} />
          </ButtonContent>
        </Button>
        <Button
          variant="outline-lg"
          isFullWidth={true}
          onClick={() => {
            goToDoctorStep();
          }}
          css={css`
            margin: 0 0 2rem 0;
          `}
        >
          <ButtonContent>
            <StethoscopeArt />
            <TextColumn>
              <Typography variant="heading4">Get from my doctor</Typography>
              <Typography
                variant="small"
                color={'capsuleBlue30'}
                css={css`
                  margin: 0.5rem 0 0 0;
                  font-weight: 300;
                `}
              >
                We'll contact your doctor to get your prescriptions.
              </Typography>
            </TextColumn>
            <StyledArrow className="arrow" src={ChevronIcon} />
          </ButtonContent>
        </Button>
        <Dialog
          title="How do transfers work?"
          description={
            <Typography variant="body">
              <p>
                Tell us which medications you want moved over and we'll take care of the rest. We'll contact your doctor
                or old pharmacy, work with your insurance, and send you a link to check out.
              </p>
              <br />
              <p>Your copay stays the same, we accept all insurance, and delivery is free.</p>
            </Typography>
          }
          triggerButton={
            <LinkWithIcon>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.system_info} />
              <Typography
                variant="small"
                css={css`
                  margin-left: ${({ theme }) => theme.space.s2};
                  position: relative;
                  display: inline-block;
                  bottom: 6px;
                `}
              >
                Same copay, free delivery. Learn more
              </Typography>
            </LinkWithIcon>
          }
        >
          <RadixDialogPrimitives.Close asChild>
            <Button variant="outline-lg" isFullWidth>
              OK
            </Button>
          </RadixDialogPrimitives.Close>
        </Dialog>
        {patients && patients.length === 1 && (
          <LinkWithIcon onClick={() => history.push(AccountPageAddDependent.toPath())} color={'capsuleGreen60'}>
            <Typography
              variant="small"
              css={css`
                position: relative;
                display: inline-block;
                bottom: 4px;
              `}
            >
              Ordering for a family member?
            </Typography>
            <StyledArrow className="arrow" src={ChevronIcon} color={'capsuleGreen60'} />
          </LinkWithIcon>
        )}
      </Content>
    </Step>
  );
};
